import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart3, Star, MessageSquare, Users, FileText, List, Download } from 'lucide-react';
import { useSurveyAnalytics } from '@/hooks/useSurveyAnalytics';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface SurveyAnalyticsDashboardProps {
  courseId: string;
}

export function SurveyAnalyticsDashboard({ courseId }: SurveyAnalyticsDashboardProps) {
  const { data: analytics, isLoading } = useSurveyAnalytics(courseId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (!analytics || analytics.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No survey data available yet.</p>
          <p className="text-sm mt-1">Create surveys and collect responses to see analytics.</p>
        </CardContent>
      </Card>
    );
  }

  const totalResponses = analytics.reduce((sum, a) => sum + a.totalResponses, 0);
  const surveysWithResponses = analytics.filter(a => a.totalResponses > 0).length;

  const exportToCSV = () => {
    if (totalResponses === 0) {
      toast.error('No responses to export');
      return;
    }

    const rows: string[][] = [];
    
    // Header row
    rows.push([
      'Survey Title',
      'Trigger Type',
      'Question',
      'Question Type',
      'Response Date',
      'Answer Text',
      'Answer Rating',
      'Answer Choice',
    ]);

    // Data rows
    analytics.forEach(surveyData => {
      const triggerType = surveyData.survey.milestone 
        ? `${surveyData.survey.milestone}% Milestone`
        : surveyData.survey.module_title 
          ? `Module: ${surveyData.survey.module_title}`
          : 'General';

      surveyData.questions.forEach(question => {
        question.answers.forEach(answer => {
          rows.push([
            surveyData.survey.title,
            triggerType,
            question.question_text,
            question.question_type,
            format(new Date(answer.completed_at), 'yyyy-MM-dd HH:mm:ss'),
            answer.answer_text || '',
            answer.answer_rating?.toString() || '',
            answer.answer_choice || '',
          ]);
        });
      });
    });

    // Convert to CSV string
    const csvContent = rows
      .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `survey-responses-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Survey responses exported!');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Survey Analytics</h3>
        </div>
        <Button variant="outline" size="sm" onClick={exportToCSV} disabled={totalResponses === 0}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics.length}</p>
                <p className="text-sm text-muted-foreground">Total Surveys</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalResponses}</p>
                <p className="text-sm text-muted-foreground">Total Responses</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <BarChart3 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{surveysWithResponses}</p>
                <p className="text-sm text-muted-foreground">Surveys with Responses</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Individual Survey Analytics */}
      <Accordion type="single" collapsible className="space-y-2">
        {analytics.map((surveyData) => (
          <AccordionItem
            key={surveyData.survey.id}
            value={surveyData.survey.id}
            className="border rounded-lg px-4"
          >
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3 text-left">
                <span className="font-medium">{surveyData.survey.title}</span>
                {surveyData.survey.milestone && (
                  <Badge variant="secondary">{surveyData.survey.milestone}%</Badge>
                )}
                {surveyData.survey.module_title && (
                  <Badge variant="outline">{surveyData.survey.module_title}</Badge>
                )}
                <Badge variant={surveyData.totalResponses > 0 ? 'default' : 'secondary'}>
                  {surveyData.totalResponses} responses
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-6">
              {surveyData.totalResponses === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No responses collected yet.
                </p>
              ) : (
                surveyData.questions.map((question, qIdx) => (
                  <div key={question.id} className="space-y-3">
                    <div className="flex items-start gap-2">
                      {question.question_type === 'text' && <MessageSquare className="h-4 w-4 mt-0.5 text-muted-foreground" />}
                      {question.question_type === 'rating' && <Star className="h-4 w-4 mt-0.5 text-muted-foreground" />}
                      {question.question_type === 'multiple_choice' && <List className="h-4 w-4 mt-0.5 text-muted-foreground" />}
                      <p className="text-sm font-medium">
                        {qIdx + 1}. {question.question_text}
                      </p>
                    </div>

                    {/* Rating Summary */}
                    {question.question_type === 'rating' && question.averageRating !== undefined && (
                      <div className="ml-6 p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-4 w-4 ${
                                  star <= Math.round(question.averageRating!)
                                    ? 'fill-primary text-primary'
                                    : 'text-muted-foreground/30'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium">
                            {question.averageRating.toFixed(1)} / 5
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({question.answers.length} responses)
                          </span>
                        </div>
                        {/* Rating distribution */}
                        <div className="space-y-1">
                          {[5, 4, 3, 2, 1].map((rating) => {
                            const count = question.answers.filter(a => a.answer_rating === rating).length;
                            const percentage = (count / question.answers.length) * 100;
                            return (
                              <div key={rating} className="flex items-center gap-2 text-xs">
                                <span className="w-3">{rating}</span>
                                <Progress value={percentage} className="h-2 flex-1" />
                                <span className="w-8 text-right text-muted-foreground">{count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Multiple Choice Summary */}
                    {question.question_type === 'multiple_choice' && question.choiceDistribution && (
                      <div className="ml-6 p-3 bg-muted/50 rounded-lg space-y-2">
                        {Object.entries(question.choiceDistribution).map(([choice, count]) => {
                          const percentage = question.answers.length > 0
                            ? (count / question.answers.length) * 100
                            : 0;
                          return (
                            <div key={choice} className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span>{choice}</span>
                                <span className="text-muted-foreground">
                                  {count} ({percentage.toFixed(0)}%)
                                </span>
                              </div>
                              <Progress value={percentage} className="h-2" />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Text Responses */}
                    {question.question_type === 'text' && question.answers.length > 0 && (
                      <div className="ml-6 space-y-2">
                        {question.answers.slice(0, 5).map((answer, idx) => (
                          <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                            <p className="text-sm">{answer.answer_text}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(new Date(answer.completed_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                        ))}
                        {question.answers.length > 5 && (
                          <p className="text-xs text-muted-foreground text-center">
                            +{question.answers.length - 5} more responses
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
